/**
 * platform/ui/src/components/SuvDefaultsPanel.js
 *
 * UI for editing SUV fallback defaults (population-mean dose, uptake time,
 * patient weight, tracer). These values are used by the SUV pipeline when
 * DICOM metadata is missing (common under anonymized public datasets).
 *
 * Mounted into CriteriaForm under RECIP 1.0; not shown for other guidelines.
 *
 * Persistence: values are mutated on the in-memory suvDefaults singleton via
 * setSuvDefaults(). They do NOT currently persist across page reloads — add
 * localStorage / Firestore persistence if your trial workflow requires it.
 */

import React, { useState } from 'react';
import {
  Typography,
  Box,
  Grid,
  TextField,
  MenuItem,
  Divider,
} from '@material-ui/core';

import {
  getSuvDefaults,
  setSuvDefaults,
} from '../../../../extensions/cornerstone/src/modules/suv/suvDefaults';

const fields = [
  {
    key: 'defaultDoseMBq_18F',
    label: '¹⁸F-PSMA mean dose',
    unit: 'MBq',
    type: 'number',
  },
  {
    key: 'defaultDoseMBq_68Ga',
    label: '⁶⁸Ga-PSMA mean dose',
    unit: 'MBq',
    type: 'number',
  },
  {
    key: 'defaultUptakeMinutes',
    label: 'Mean uptake time',
    unit: 'min',
    type: 'number',
  },
  {
    key: 'defaultPatientWeightKg',
    label: 'Default patient weight',
    unit: 'kg',
    type: 'number',
  },
  {
    key: 'defaultTracer',
    label: 'Default tracer',
    unit: '',
    type: 'select',
    options: [
      ['F-18', '¹⁸F-PSMA'],
      ['Ga-68', '⁶⁸Ga-PSMA'],
    ],
  },
  {
    key: 'enableFallback',
    label: 'Enable fallback',
    unit: '',
    type: 'select',
    options: [
      ['true', 'Enabled'],
      ['false', 'Disabled (strict DICOM)'],
    ],
  },
];

export default function SuvDefaultsPanel() {
  const [defs, setDefs] = useState(getSuvDefaults());

  const onChange = (key, raw) => {
    const f = fields.find(x => x.key === key);
    let value;
    if (f.type === 'number') {
      value = Number(raw);
      if (!isFinite(value) || value <= 0) return;
    } else if (key === 'enableFallback') {
      value = raw === 'true';
    } else {
      value = raw;
    }
    const next = { ...defs, [key]: value };
    setDefs(next);
    setSuvDefaults({ [key]: value });
  };

  return (
    <Box>
      <Typography variant="subtitle1" color="primary" sx={{ mt: 2 }}>
        SUV Defaults (used when DICOM metadata is missing)
      </Typography>
      <Typography
        variant="caption"
        sx={{ color: 'var(--default-color)', display: 'block', mb: 1 }}
      >
        Population means from LMU PSMA-PET dataset (Jeblick et al. Sci Data
        2026): ¹⁸F-PSMA 246 MBq / 74 min uptake (369 studies); ⁶⁸Ga-PSMA 214 MBq
        / 74 min uptake (228 studies). Override per trial as needed.
      </Typography>
      <Divider />
      <Grid container spacing={1} sx={{ padding: '10px 0' }}>
        {fields.map(f => {
          const currentVal =
            f.key === 'enableFallback' ? String(defs[f.key]) : defs[f.key];
          return (
            <Grid item xs={6} key={f.key}>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                sx={{ padding: '5px 20px' }}
              >
                <Typography color="primary">{f.label}:</Typography>
                <Box
                  display="flex"
                  alignItems="center"
                  style={{ width: '180px', margin: '5px 0' }}
                >
                  {f.type === 'select' ? (
                    <TextField
                      select
                      value={currentVal}
                      onChange={e => onChange(f.key, e.target.value)}
                      margin="dense"
                      fullWidth
                    >
                      {f.options.map(([value, label]) => (
                        <MenuItem key={value} value={value}>
                          {label}
                        </MenuItem>
                      ))}
                    </TextField>
                  ) : (
                    <>
                      <TextField
                        type="number"
                        value={currentVal}
                        onChange={e => onChange(f.key, e.target.value)}
                        margin="dense"
                      />
                      {f.unit && (
                        <Typography sx={{ marginLeft: '8px' }}>
                          {f.unit}
                        </Typography>
                      )}
                    </>
                  )}
                </Box>
              </Box>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
}
